// TL-6 Marker: the markup tool. Cloud = 2-click scalloped revision cloud; highlighter =
// freehand translucent stroke. Both land as MarkerEntity on the active layer.
import { dist, type Vec } from "../core/geometry";
import { cloudScallops } from "../core/marker";
import { simplifyRDP } from "../core/simplify";
import { newId, defaultStyle } from "../doc/document";
import type { MarkerEntity } from "../doc/entities";
import { strokeWidthWorld } from "../engine/renderEntities";
import type { RenderView } from "../engine/renderEntities";
import type { Tool, ToolCtx, ToolKeyEvent, ToolPointerEvent } from "./types";

export type MarkerKind = "cloud" | "highlight";
const CLOUD_R = 6; // mm

export class MarkerTool implements Tool {
  readonly id = "marker" as const;
  private kind: MarkerKind = "cloud";
  private anchor: Vec | null = null; // cloud first corner
  private points: Vec[] = []; // highlight capture
  private capturing = false;
  private cursor: Vec | null = null;

  constructor(private ctx: ToolCtx) {}

  setKind(kind: MarkerKind): void {
    this.kind = kind;
  }

  activate(): void {
    this.reset();
  }

  deactivate(): void {
    this.reset();
  }

  cancel(): void {
    this.reset();
  }

  inProgress(): boolean {
    return this.anchor !== null || this.capturing;
  }

  private reset(): void {
    this.anchor = null;
    this.points = [];
    this.capturing = false;
    this.cursor = null;
    this.ctx.setMeasure("none", "");
    this.ctx.setSnapBase(null);
    this.ctx.gestureActive(false);
    this.ctx.setHint(
      this.kind === "cloud"
        ? "Cloud: click two opposite corners to draw a revision cloud"
        : "Highlighter: press and drag to highlight · release to finish"
    );
    this.ctx.setCursorTip(null);
    this.ctx.requestRender();
  }

  pointerDown(e: ToolPointerEvent): void {
    if (e.button !== 0) return;
    if (this.kind === "cloud") {
      if (!this.anchor) {
        this.anchor = e.point;
        this.ctx.setSnapBase(e.point);
        this.ctx.setHint("Cloud: click the opposite corner");
        this.ctx.setCursorTip("opposite corner");
      } else {
        this.commitCloud(e.point);
      }
    } else {
      this.capturing = true;
      this.points = [e.world];
      this.ctx.gestureActive(true);
    }
  }

  pointerMove(e: ToolPointerEvent): void {
    this.cursor = e.point;
    if (this.capturing) {
      const last = this.points[this.points.length - 1]!;
      if (dist(e.world, last) * this.ctx.scale() >= 2) this.points.push(e.world);
    }
    this.ctx.requestRender();
  }

  pointerUp(): void {
    if (this.kind === "highlight" && this.capturing) {
      this.capturing = false;
      this.ctx.gestureActive(false);
      const simplified = simplifyRDP(this.points, this.ctx.prefs().freehandTolPx / this.ctx.scale());
      this.points = [];
      if (simplified.length >= 2) this.commitHighlight(simplified);
      this.ctx.requestRender();
    }
  }

  doubleClick(): void {}

  key(e: ToolKeyEvent): boolean {
    if (e.key === "Escape" && this.inProgress()) {
      this.reset();
      return true;
    }
    return false;
  }

  measurement(): boolean {
    return false;
  }

  private commitCloud(opposite: Vec): void {
    const a = this.anchor!;
    if (Math.abs(opposite.x - a.x) < CLOUD_R || Math.abs(opposite.y - a.y) < CLOUD_R) return; // too small
    const entity: MarkerEntity = {
      id: newId(),
      type: "marker",
      kind: "cloud",
      layerId: this.ctx.activeLayerId(),
      locked: false,
      hidden: false,
      points: [a, opposite],
      style: { ...defaultStyle(), strokeColor: "#e05c5c", strokeWidth: 0.5 }
    };
    this.ctx.store.dispatch({ kind: "addEntities", entities: [entity], z: [{ layerId: entity.layerId, index: -1 }] });
    this.ctx.selection.set([entity.id]);
    this.reset();
  }

  private commitHighlight(points: Vec[]): void {
    const h = this.ctx.prefs().highlighter; // F2: size/colour/opacity are user settings
    const entity: MarkerEntity = {
      id: newId(),
      type: "marker",
      kind: "highlight",
      layerId: this.ctx.activeLayerId(),
      locked: false,
      hidden: false,
      points,
      style: {
        ...defaultStyle(),
        strokeColor: h.color,
        strokeWidth: h.width,
        widthMode: "screen",
        opacity: h.opacity
      }
    };
    this.ctx.store.dispatch({ kind: "addEntities", entities: [entity], z: [{ layerId: entity.layerId, index: -1 }] });
  }

  renderDynamic(v: RenderView): void {
    const { ctx } = v;
    if (this.kind === "cloud" && this.anchor && this.cursor) {
      const pts = cloudScallops(this.anchor, this.cursor, CLOUD_R);
      if (pts.length >= 2) {
        ctx.strokeStyle = "#e05c5c";
        ctx.lineWidth = 0.5;
        ctx.lineJoin = "round";
        ctx.beginPath();
        ctx.moveTo(pts[0]!.x, pts[0]!.y);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i]!.x, pts[i]!.y);
        ctx.closePath();
        ctx.stroke();
      }
    } else if (this.kind === "highlight" && this.points.length >= 2) {
      const h = this.ctx.prefs().highlighter;
      const style = { ...defaultStyle(), strokeColor: h.color, strokeWidth: h.width, widthMode: "screen" as const };
      ctx.globalAlpha = h.opacity;
      ctx.strokeStyle = h.color;
      ctx.lineWidth = strokeWidthWorld(style, v.cam, v.dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      ctx.moveTo(this.points[0]!.x, this.points[0]!.y);
      for (let i = 1; i < this.points.length; i++) ctx.lineTo(this.points[i]!.x, this.points[i]!.y);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
  }
}
